// Roster print & share sheet. Matches the "Culinaire Specialist Operations"
// document aesthetic used by inventoryPdf.js: obsidian background, champagne
// gold accents, framed corners, Fraunces wordmark. Two layouts:
//   mode = "week"  -> staff-by-day matrix with per-row totals
//   mode = "month" -> calendar grid with shifts per date + per-staff summary
//
// Opens in a new window with a floating toolbar:
//   Print / Save as PDF, Copy summary, Share (Web Share API when available).

import {
  computeShiftCost,
  isoDate,
  addDays,
  monthGridDays,
  startOfMonth,
  endOfMonth,
} from "./labor";
import { departmentForRole, departmentLabel } from "./departments";
import { getCurrency } from "./prefs";

const FMT_MONEY = (v, currency) => {
  const cur = currency || getCurrency() || "USD";
  try {
    return Number(v || 0).toLocaleString(undefined, { style: "currency", currency: cur, maximumFractionDigits: 2 });
  } catch {
    return `${cur} ${(Number(v) || 0).toFixed(2)}`;
  }
};

const FMT_HRS = (n) => `${(Number(n) || 0).toFixed(2)}h`;

function escapeHtml(v) {
  const s = String(v ?? "");
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function fmtDateLong(d) {
  return new Date(d).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

function fmtDateShort(d) {
  return new Date(d).toLocaleDateString(undefined, { weekday: "short", month: "numeric", day: "numeric" });
}

function fmtDay(d) {
  return new Date(d).toLocaleDateString(undefined, { weekday: "short" });
}

function fmtRange(start, end) {
  return `${new Date(start).toLocaleDateString(undefined, { month: "short", day: "numeric" })} to ${new Date(end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
}

function fmtMonth(d) {
  return new Date(d).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function shiftLine(sh) {
  const start = (sh.start_time || "").slice(0, 5);
  const end = (sh.end_time || "").slice(0, 5);
  return `${start} to ${end}`;
}

function groupBy(list, keyFn) {
  const out = {};
  for (const item of list) {
    const k = keyFn(item);
    (out[k] = out[k] || []).push(item);
  }
  return out;
}

// Builds an HTML block summarising cost per FOH / BOH / Other for the
// shifts in the current view. Only shows People, Shifts, and Labor Cost.
// Hours are computed internally for cost math but never printed, matching
// the rest of the print sheet where hours stay off-page. Labor Cost still
// follows the `cost-only` toggle.
function renderDepartmentSummary({ shifts, staffById, currency }) {
  const buckets = {
    FOH: { hours: 0, cost: 0, shifts: 0, staffIds: new Set() },
    BOH: { hours: 0, cost: 0, shifts: 0, staffIds: new Set() },
    Other: { hours: 0, cost: 0, shifts: 0, staffIds: new Set() },
  };
  for (const sh of shifts) {
    const staff = staffById[sh.staff_id];
    const dep = departmentForRole(staff?.role);
    const c = computeShiftCost(sh, staffById);
    buckets[dep].hours += c.hours;
    buckets[dep].cost += c.cost;
    buckets[dep].shifts += 1;
    if (sh.staff_id) buckets[dep].staffIds.add(sh.staff_id);
  }
  const rows = ["FOH", "BOH", "Other"]
    .filter((code) => buckets[code].shifts > 0)
    .map((code) => {
      const b = buckets[code];
      return `
        <tr>
          <td class="cell name">
            <div class="staff-name">${escapeHtml(departmentLabel(code))}</div>
            <div class="staff-role">${escapeHtml(code)}</div>
          </td>
          <td class="cell right muted">${b.staffIds.size}</td>
          <td class="cell right muted">${b.shifts}</td>
          <td class="cell right tabular cost-only">${FMT_MONEY(b.cost, currency)}</td>
        </tr>`;
    })
    .join("");
  if (!rows) return "";
  const totalCost = Object.values(buckets).reduce((n, b) => n + b.cost, 0);
  const totalShifts = Object.values(buckets).reduce((n, b) => n + b.shifts, 0);
  const totalStaff = new Set(
    Object.values(buckets).flatMap((b) => Array.from(b.staffIds)),
  ).size;
  return `
    <div class="section">
      <div class="label">Sub-department summary</div>
      <table class="grid summary-grid">
        <thead>
          <tr>
            <th class="staff-col">Department</th>
            <th class="right">People</th>
            <th class="right">Shifts</th>
            <th class="right cost-only">Labor cost</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr>
            <td class="cell name total-label">Total</td>
            <td class="cell right total tabular">${totalStaff}</td>
            <td class="cell right total tabular">${totalShifts}</td>
            <td class="cell right total tabular cost-only">${FMT_MONEY(totalCost, currency)}</td>
          </tr>
        </tfoot>
      </table>
    </div>`;
}

// -----------------------------------------------------------------------
// Weekly layout: staff rows x 7 day columns
// -----------------------------------------------------------------------
function renderWeekly({ weekStart, staff, shifts, staffById, currency }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const dayIso = days.map(isoDate);
  const shiftsByStaffDay = {};
  for (const sh of shifts) {
    const key = `${sh.staff_id}:${sh.date}`;
    (shiftsByStaffDay[key] = shiftsByStaffDay[key] || []).push(sh);
  }

  let grandHrs = 0;
  let grandCost = 0;
  const rows = staff
    .filter((s) => s.active !== false)
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    .map((s) => {
      let rowHrs = 0;
      let rowCost = 0;
      const cells = dayIso.map((iso) => {
        const list = shiftsByStaffDay[`${s.id}:${iso}`] || [];
        if (list.length === 0) return `<td class="cell day"><span class="off">off</span></td>`;
        const lines = list.map((sh) => {
          const c = computeShiftCost(sh, staffById);
          rowHrs += c.hours;
          rowCost += c.cost;
          return `<div class="shift"><div class="times">${escapeHtml(shiftLine(sh))}</div></div>`;
        }).join("");
        return `<td class="cell day filled">${lines}</td>`;
      }).join("");
      grandHrs += rowHrs;
      grandCost += rowCost;
      return `
        <tr>
          <td class="cell name">
            <div class="staff-name">${escapeHtml(s.name || "-")}</div>
            <div class="staff-role">${escapeHtml(s.role || "")}</div>
          </td>
          ${cells}
          <td class="cell right total tabular cost-only">${FMT_MONEY(rowCost, currency)}</td>
        </tr>`;
    })
    .join("");

  const dayHeaders = days.map((d) => `
    <th>
      <div class="dow">${escapeHtml(fmtDay(d))}</div>
      <div class="dom">${escapeHtml(new Date(d).toLocaleDateString(undefined, { month: "numeric", day: "numeric" }))}</div>
    </th>`).join("");

  const activeCount = staff.filter((s) => s.active !== false).length;
  const shiftCount = shifts.filter((sh) => dayIso.includes(sh.date)).length;
  return {
    subtitle: `Week of ${fmtRange(days[0], days[6])}`,
    heroLabel: "Total labor cost this week",
    heroValue: FMT_MONEY(grandCost, currency),
    heroSub: `${shiftCount} shift${shiftCount === 1 ? "" : "s"} scheduled across ${activeCount} team member${activeCount === 1 ? "" : "s"}`,
    tableHtml: `
      <table class="grid week-grid">
        <thead>
          <tr>
            <th class="staff-col">Team member</th>
            ${dayHeaders}
            <th class="right cost-only">Labor cost</th>
          </tr>
        </thead>
        <tbody>${rows || `<tr><td colspan="9" class="cell muted center">No active team members.</td></tr>`}</tbody>
        <tfoot class="cost-only">
          <tr>
            <td class="cell name total-label">Total</td>
            <td class="cell" colspan="7"></td>
            <td class="cell right total tabular">${FMT_MONEY(grandCost, currency)}</td>
          </tr>
        </tfoot>
      </table>`,
    grandHrs,
    grandCost,
  };
}

// -----------------------------------------------------------------------
// Monthly layout: calendar of days with shift chips + per-staff summary
// -----------------------------------------------------------------------
function renderMonthly({ monthAnchor, staff, shifts, staffById, currency }) {
  const days = monthGridDays(monthAnchor);
  const targetMonth = new Date(monthAnchor).getMonth();
  const dayIsoSet = new Set(days.map(isoDate));

  const shiftsByDay = groupBy(shifts, (s) => s.date);

  let grandHrs = 0;
  let grandCost = 0;
  const staffAgg = {};
  for (const sh of shifts) {
    if (!dayIsoSet.has(sh.date)) continue;
    const c = computeShiftCost(sh, staffById);
    grandHrs += c.hours;
    grandCost += c.cost;
    const rec = staffAgg[sh.staff_id] || { hours: 0, cost: 0, shifts: 0 };
    rec.hours += c.hours;
    rec.cost += c.cost;
    rec.shifts += 1;
    staffAgg[sh.staff_id] = rec;
  }

  // Build 7-column calendar rows
  const rowsHtml = [];
  for (let i = 0; i < days.length; i += 7) {
    const week = days.slice(i, i + 7);
    const cells = week.map((d) => {
      const iso = isoDate(d);
      const inMonth = new Date(d).getMonth() === targetMonth;
      const dayShifts = shiftsByDay[iso] || [];
      const chips = dayShifts.slice(0, 4).map((sh) => {
        const c = computeShiftCost(sh, staffById);
        const name = (c.staff?.name || "-").split(" ")[0];
        return `<div class="chip"><span class="chip-name">${escapeHtml(name)}</span><span class="chip-times">${escapeHtml(shiftLine(sh))}</span></div>`;
      }).join("");
      const more = dayShifts.length > 4 ? `<div class="chip more">+${dayShifts.length - 4} more</div>` : "";
      return `<td class="cell m-day ${inMonth ? "" : "muted"}">
        <div class="m-date">${new Date(d).getDate()}</div>
        <div class="m-chips">${chips}${more}</div>
      </td>`;
    }).join("");
    rowsHtml.push(`<tr>${cells}</tr>`);
  }

  const dowHeaders = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    .map((d) => `<th>${d}</th>`).join("");

  const staffRows = Object.entries(staffAgg)
    .map(([sid, rec]) => ({ sid, ...rec, staff: staffById[sid] }))
    .filter((r) => r.staff)
    .sort((a, b) => (a.staff.name || "").localeCompare(b.staff.name || ""))
    .map((r) => `
      <tr>
        <td class="cell name">
          <div class="staff-name">${escapeHtml(r.staff.name || "-")}</div>
          <div class="staff-role">${escapeHtml(r.staff.role || "")}</div>
        </td>
        <td class="cell right muted">${r.shifts}</td>
        <td class="cell right tabular cost-only">${FMT_MONEY(r.cost, currency)}</td>
      </tr>`).join("");

  const monthShiftCount = shifts.filter((s) => dayIsoSet.has(s.date)).length;
  const monthStaffCount = Object.keys(staffAgg).length;
  return {
    subtitle: fmtMonth(monthAnchor),
    heroLabel: "Total labor cost this month",
    heroValue: FMT_MONEY(grandCost, currency),
    heroSub: `${monthShiftCount} shift${monthShiftCount === 1 ? "" : "s"} scheduled across ${monthStaffCount} team member${monthStaffCount === 1 ? "" : "s"}`,
    tableHtml: `
      <table class="grid month-grid">
        <thead><tr>${dowHeaders}</tr></thead>
        <tbody>${rowsHtml.join("")}</tbody>
      </table>
      <div class="section">
        <div class="label">Per team member summary</div>
        <table class="grid summary-grid">
          <thead>
            <tr>
              <th class="staff-col">Team member</th>
              <th class="right">Shifts</th>
              <th class="right cost-only">Labor cost</th>
            </tr>
          </thead>
          <tbody>${staffRows || `<tr><td colspan="3" class="cell muted center">No shifts logged this month.</td></tr>`}</tbody>
          <tfoot>
            <tr>
              <td class="cell name total-label">Total</td>
              <td class="cell right total tabular">${monthShiftCount}</td>
              <td class="cell right total tabular cost-only">${FMT_MONEY(grandCost, currency)}</td>
            </tr>
          </tfoot>
        </table>
      </div>`,
    grandHrs,
    grandCost,
  };
}

// -----------------------------------------------------------------------
// Plain-text summary for share / clipboard
// -----------------------------------------------------------------------
function buildSummaryText({ mode, weekStart, monthAnchor, staff, shifts, staffById, restaurant, currency, includeCost }) {
  const lines = [];
  const period = mode === "week"
    ? `Week of ${fmtRange(weekStart, addDays(weekStart, 6))}`
    : fmtMonth(monthAnchor);
  lines.push(`${restaurant} : Roster`);
  lines.push(period);
  lines.push("");

  if (mode === "week") {
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    for (const d of days) {
      const iso = isoDate(d);
      const dayShifts = shifts.filter((s) => s.date === iso);
      if (dayShifts.length === 0) {
        lines.push(`${fmtDateLong(d)}`);
        lines.push("  no shifts");
        continue;
      }
      lines.push(`${fmtDateLong(d)}`);
      for (const sh of dayShifts) {
        const c = computeShiftCost(sh, staffById);
        const name = c.staff?.name || "unassigned";
        lines.push(`  ${name} : ${shiftLine(sh)}`);
      }
    }
  } else {
    const from = isoDate(startOfMonth(monthAnchor));
    const to = isoDate(endOfMonth(monthAnchor));
    const inMonth = shifts.filter((s) => s.date >= from && s.date <= to);
    const byDate = groupBy(inMonth, (s) => s.date);
    for (const iso of Object.keys(byDate).sort()) {
      lines.push(fmtDateShort(iso));
      for (const sh of byDate[iso]) {
        const c = computeShiftCost(sh, staffById);
        const name = c.staff?.name || "unassigned";
        lines.push(`  ${name} : ${shiftLine(sh)}`);
      }
    }
  }

  let totalCost = 0;
  const targetShifts = mode === "week"
    ? shifts.filter((s) => {
        const iso = isoDate(new Date(s.date));
        const inWeek = Array.from({ length: 7 }, (_, i) => isoDate(addDays(weekStart, i)));
        return inWeek.includes(iso);
      })
    : shifts.filter((s) => {
        const from = isoDate(startOfMonth(monthAnchor));
        const to = isoDate(endOfMonth(monthAnchor));
        return s.date >= from && s.date <= to;
      });
  for (const sh of targetShifts) {
    const c = computeShiftCost(sh, staffById);
    totalCost += c.cost;
  }
  if (includeCost) {
    lines.push("");
    lines.push(`Labor cost: ${FMT_MONEY(totalCost, currency)}`);
  }
  return lines.join("\n");
}

// -----------------------------------------------------------------------
// CSV builder: one row per shift so payroll can sort/filter in Sheets/Excel.
// Excel's CSV import treats a leading BOM as UTF-8, keeps accented staff
// names intact. Cells with commas or quotes are quoted per RFC 4180.
// -----------------------------------------------------------------------
function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildCsv({ mode, weekStart, monthAnchor, staff, shifts, staffById, restaurant, currency, includeCost }) {
  const cur = currency || getCurrency() || "USD";
  const rangeShifts = mode === "week"
    ? (() => {
        const inWeek = new Set(Array.from({ length: 7 }, (_, i) => isoDate(addDays(weekStart, i))));
        return shifts.filter((s) => inWeek.has(s.date));
      })()
    : (() => {
        const from = isoDate(startOfMonth(monthAnchor));
        const to = isoDate(endOfMonth(monthAnchor));
        return shifts.filter((s) => s.date >= from && s.date <= to);
      })();

  const sorted = [...rangeShifts].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    return (a.start_time || "").localeCompare(b.start_time || "");
  });

  // Header: cost columns dropped when the admin has not opted in to sharing pay.
  const header = ["Date", "Day", "Staff", "Role", "Start", "End", "Break (min)", "Hours"];
  if (includeCost) {
    header.push(`Rate (${cur})`, `Labor cost (${cur})`);
  }
  const rows = [header.map(csvEscape).join(",")];

  let totalHrs = 0;
  let totalCost = 0;
  for (const sh of sorted) {
    const c = computeShiftCost(sh, staffById);
    const s = c.staff || {};
    totalHrs += c.hours;
    totalCost += c.cost;
    // Wrap ISO date as an Excel text literal so Excel keeps the string at
    // natural width instead of auto-formatting into a wider date value that
    // shows as `########` in a default-width column.
    const dateCell = `="${sh.date}"`;
    const base = [
      dateCell,
      fmtDay(sh.date),
      s.name || "unassigned",
      s.role || "",
      (sh.start_time || "").slice(0, 5),
      (sh.end_time || "").slice(0, 5),
      sh.break_minutes || 0,
      c.hours.toFixed(2),
    ];
    if (includeCost) {
      base.push(c.rate.toFixed(2), c.cost.toFixed(2));
    }
    rows.push(base.map(csvEscape).join(","));
  }

  // Blank row + totals footer.
  rows.push("");
  const totals = ["", "", "", "", "", "", "Total", totalHrs.toFixed(2)];
  if (includeCost) {
    totals.push("", totalCost.toFixed(2));
  }
  rows.push(totals.map(csvEscape).join(","));

  return "\ufeff" + rows.join("\r\n");
}

function csvFilename({ mode, weekStart, monthAnchor, restaurant }) {
  const safe = String(restaurant || "roster").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "roster";
  if (mode === "week") {
    return `${safe}-roster-week-${isoDate(weekStart)}.csv`;
  }
  const d = new Date(monthAnchor);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${safe}-roster-month-${y}-${m}.csv`;
}

// -----------------------------------------------------------------------
// Public entry
// -----------------------------------------------------------------------
export function printRosterSchedule({ mode, weekStart, monthAnchor, staff, shifts, restaurant, currency }) {
  const win = window.open("", "_blank", "width=1280,height=900");
  if (!win) return;
  const staffById = Object.fromEntries(staff.map((s) => [s.id, s]));
  const cur = currency || getCurrency() || "USD";

  const generatedAt = new Date();
  const dateStr = generatedAt.toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });

  const layout = mode === "week"
    ? renderWeekly({ weekStart, staff, shifts, staffById, currency: cur })
    : renderMonthly({ monthAnchor, staff, shifts, staffById, currency: cur });

  // Filter shifts to the currently-visible period so the sub-department
  // summary matches the on-screen cards exactly.
  const periodShifts = mode === "week"
    ? (() => {
        const inWeek = new Set(Array.from({ length: 7 }, (_, i) => isoDate(addDays(weekStart, i))));
        return shifts.filter((s) => inWeek.has(s.date));
      })()
    : (() => {
        const from = isoDate(startOfMonth(monthAnchor));
        const to = isoDate(endOfMonth(monthAnchor));
        return shifts.filter((s) => s.date >= from && s.date <= to);
      })();
  const departmentHtml = renderDepartmentSummary({
    shifts: periodShifts,
    staffById,
    currency: cur,
  });

  const summaryTextWithCost = buildSummaryText({
    mode, weekStart, monthAnchor, staff, shifts, staffById,
    restaurant: restaurant || "Kitchen", currency: cur, includeCost: true,
  });
  const summaryTextNoCost = buildSummaryText({
    mode, weekStart, monthAnchor, staff, shifts, staffById,
    restaurant: restaurant || "Kitchen", currency: cur, includeCost: false,
  });

  const csvWithCost = buildCsv({
    mode, weekStart, monthAnchor, staff, shifts, staffById,
    restaurant: restaurant || "Kitchen", currency: cur, includeCost: true,
  });
  const csvNoCost = buildCsv({
    mode, weekStart, monthAnchor, staff, shifts, staffById,
    restaurant: restaurant || "Kitchen", currency: cur, includeCost: false,
  });
  const csvName = csvFilename({ mode, weekStart, monthAnchor, restaurant: restaurant || "Kitchen" });

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Roster ${escapeHtml(layout.subtitle)} : ${escapeHtml(restaurant || "Kitchen")}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600&family=Inter+Tight:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  @page { size: A4 landscape; margin: 0; }
  html, body { margin: 0; padding: 0; background: #0b0b0d; color: #f5efe0; font-family: 'Inter Tight', -apple-system, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .sheet {
    box-sizing: border-box;
    width: 297mm; min-height: 210mm;
    padding: 14mm 16mm 18mm;
    background:
      radial-gradient(ellipse at top right, rgba(232,207,154,0.10), transparent 55%),
      linear-gradient(180deg, #0d0c0a 0%, #08070a 100%);
    position: relative;
  }
  .corner { position: absolute; width: 14mm; height: 14mm; border: 1.2px solid rgba(232,207,154,0.65); }
  .corner.tl { top: 7mm; left: 7mm; border-right: none; border-bottom: none; }
  .corner.tr { top: 7mm; right: 7mm; border-left: none; border-bottom: none; }
  .corner.bl { bottom: 7mm; left: 7mm; border-right: none; border-top: none; }
  .corner.br { bottom: 7mm; right: 7mm; border-left: none; border-top: none; }

  .brand { display: flex; align-items: center; gap: 8px; font-size: 10px; letter-spacing: 0.28em; text-transform: uppercase; color: #c8a96a; }
  .brand svg { width: 4mm; height: 4mm; color: #f2dfb5; }
  h1 { font-family: 'Fraunces', Georgia, serif; font-weight: 400; font-size: 32px; line-height: 1.05; margin: 6mm 0 2mm; color: #f5efe0; letter-spacing: -0.01em; }
  .subtitle { font-family: 'Fraunces', serif; font-style: italic; font-size: 15px; color: #c8a96a; margin: 0; }
  .meta { display: flex; justify-content: space-between; margin-top: 6mm; font-size: 10px; color: #8a8578; letter-spacing: 0.16em; text-transform: uppercase; }
  .meta .value { color: #f2dfb5; text-transform: none; letter-spacing: 0; font-family: 'Fraunces', serif; font-size: 13px; margin-top: 1mm; }

  .value-hero { margin-top: 6mm; padding: 5mm 6mm; border-radius: 3mm; background: linear-gradient(180deg, rgba(232,207,154,0.10), rgba(232,207,154,0.02)); border: 1px solid rgba(232,207,154,0.35); display:flex; justify-content: space-between; align-items: baseline; gap: 8mm; }
  .value-hero .eyebrow { font-size: 9px; letter-spacing: 0.28em; text-transform: uppercase; color: #c8a96a; }
  .value-hero .amount { font-family: 'Fraunces', serif; font-weight: 400; font-size: 38px; letter-spacing: -0.02em; color: #f5efe0; margin-top: 1mm; }
  .value-hero .sub { font-size: 11px; color: #8a8578; margin-top: 1mm; }

  .section { margin-top: 6mm; }
  .section .label { font-size: 9px; letter-spacing: 0.28em; text-transform: uppercase; color: #c8a96a; margin-bottom: 3mm; }

  table.grid { width: 100%; border-collapse: collapse; font-variant-numeric: tabular-nums; }
  thead th { padding: 2.5mm 2mm; font-size: 8.5px; letter-spacing: 0.2em; text-transform: uppercase; color: #6f6a5e; border-bottom: 1px solid rgba(232,207,154,0.25); text-align: left; }
  thead th.right { text-align: right; }
  th.staff-col { min-width: 34mm; }
  .cell { padding: 2mm 2mm; font-size: 10.5px; border-bottom: 1px solid #1a1a1e; vertical-align: top; }
  .cell.right { text-align: right; }
  .cell.center { text-align: center; }
  .cell.muted { color: #6f6a5e; }
  .cell.name .staff-name { color: #f5efe0; font-weight: 500; }
  .cell.name .staff-role { color: #8a8578; font-size: 9px; margin-top: 0.5mm; letter-spacing: 0.06em; text-transform: uppercase; }
  .cell.tabular { font-variant-numeric: tabular-nums; }
  tfoot .cell.total { color: #f5efe0; font-weight: 600; border-top: 1px solid rgba(232,207,154,0.35); }
  tfoot .total-label { color: #c8a96a; text-transform: uppercase; letter-spacing: 0.18em; font-size: 9.5px; }

  /* Weekly */
  .week-grid th .dow { font-size: 9px; letter-spacing: 0.18em; color: #c8a96a; }
  .week-grid th .dom { font-family: 'Fraunces', serif; font-size: 14px; color: #f5efe0; margin-top: 0.5mm; }
  .week-grid .cell.day { min-width: 26mm; text-align: right; }
  .week-grid .cell.day .off { font-size: 9.5px; color: #4a463d; letter-spacing: 0.08em; text-transform: uppercase; }
  .week-grid .cell.day .shift { display: flex; justify-content: space-between; align-items: baseline; gap: 3mm; padding: 0.6mm 0; }
  .week-grid .cell.day .times { font-size: 10.5px; color: #f5efe0; }
  .week-grid .cell.day .hrs { font-size: 9.5px; color: #c8a96a; }
  .week-grid .day-total { color: #c8a96a; font-weight: 600; }

  /* Monthly */
  .month-grid th { text-align: center; font-family: 'Fraunces', serif; font-size: 13px; color: #c8a96a; letter-spacing: 0.06em; text-transform: none; padding-bottom: 2mm; }
  .month-grid td.m-day { width: 14.28%; height: 24mm; vertical-align: top; padding: 1.5mm 2mm; }
  .month-grid td.m-day.muted { background: rgba(255,255,255,0.02); color: #4a463d; }
  .month-grid td.m-day .m-date { font-family: 'Fraunces', serif; font-size: 14px; color: #f5efe0; text-align: right; margin-bottom: 1mm; }
  .month-grid td.m-day.muted .m-date { color: #4a463d; }
  .month-grid td.m-day .m-chips { display: flex; flex-direction: column; gap: 0.6mm; }
  .chip { display: flex; justify-content: space-between; gap: 2mm; font-size: 8.5px; padding: 0.6mm 1.5mm; background: rgba(232,207,154,0.10); border: 1px solid rgba(232,207,154,0.28); border-radius: 1.2mm; }
  .chip-name { color: #f5efe0; font-weight: 500; }
  .chip-times { color: #c8a96a; }
  .chip.more { justify-content: center; background: rgba(255,255,255,0.03); border-color: rgba(255,255,255,0.10); color: #8a8578; }

  .footer { position: absolute; left: 16mm; right: 16mm; bottom: 10mm; display: flex; justify-content: space-between; align-items: end; font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; color: #6f6a5e; }
  .footer .value { color: #c8a96a; text-transform: none; letter-spacing: 0; font-family: 'Fraunces', serif; font-size: 12px; }

  .toolbar { position: fixed; top: 12px; right: 12px; z-index: 100; display: flex; gap: 8px; }
  .toolbar button { background: #c8a96a; color: #0b0b0d; border: none; padding: 8px 14px; border-radius: 999px; font-weight: 600; font-size: 11px; cursor: pointer; font-family: 'Inter Tight', sans-serif; letter-spacing: 0.02em; }
  .toolbar button.ghost { background: rgba(255,255,255,0.10); color: #f5efe0; border: 1px solid rgba(255,255,255,0.20); }
  .toolbar button:hover { filter: brightness(1.08); }
  .toolbar .ok { position: absolute; right: 0; top: 42px; background: rgba(200,169,106,0.15); color: #f2dfb5; padding: 6px 12px; border-radius: 6px; font-size: 11px; opacity: 0; transition: opacity 0.25s; pointer-events: none; white-space: nowrap; }
  .toolbar .ok.show { opacity: 1; }
  @media print { .toolbar { display: none; } }

  /* Cost visibility gate: labor cost columns, headers, and hero swap based
     on the Include labor cost toggle. Default: OFF (never share money
     with the team). Toggle ON to reveal for admin printing only. Hours
     stay off the sheet at all times: never printed, never shared. */
  body:not(.show-cost) .cost-only { display: none !important; }
  body:not(.show-cost) .hero-neutral { display: block; }
  body.show-cost .hero-neutral { display: none; }
  body.show-cost .hero-cost { display: block; }
  body:not(.show-cost) .hero-cost { display: none; }
</style></head>
<body>
  <div class="toolbar">
    <button onclick="window.print()">Print / Save as PDF</button>
    <button class="ghost" id="csv-btn">Download CSV</button>
    <button class="ghost" id="copy-btn">Copy summary</button>
    <button class="ghost" id="share-btn" style="display:none">Share</button>
    <label class="ghost cost-toggle" style="display:inline-flex;align-items:center;gap:6px;padding:6px 10px;border-radius:999px;border:1px solid rgba(255,255,255,0.20);background:rgba(255,255,255,0.06);color:#f5efe0;font-size:11px;letter-spacing:0.02em;cursor:pointer;">
      <input id="cost-toggle" type="checkbox" style="cursor:pointer" />
      Include labor cost
    </label>
    <div class="ok" id="ok"></div>
  </div>

  <div class="sheet">
    <div class="corner tl"></div><div class="corner tr"></div>
    <div class="corner bl"></div><div class="corner br"></div>

    <div class="brand">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
        <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z"/>
        <line x1="6" y1="17" x2="18" y2="17"/>
      </svg>
      Culinaire Specialist : Operations
    </div>

    <h1>Roster : ${mode === "week" ? "Weekly" : "Monthly"}</h1>
    <div class="subtitle">${escapeHtml(restaurant || "Kitchen")} : ${escapeHtml(layout.subtitle)}</div>

    <div class="meta">
      <div><div>Generated</div><div class="value">${escapeHtml(dateStr)}</div></div>
    </div>

    <div class="value-hero">
      <div>
        <div class="eyebrow hero-neutral">Team roster</div>
        <div class="eyebrow hero-cost">${escapeHtml(layout.heroLabel)}</div>
        <div class="amount hero-cost">${escapeHtml(layout.heroValue)}</div>
        <div class="sub">${escapeHtml(layout.heroSub)}</div>
      </div>
    </div>

    <div class="section">
      <div class="label">${mode === "week" ? "Weekly schedule" : "Monthly schedule"}</div>
      ${layout.tableHtml}
    </div>

    ${departmentHtml}

    <div class="footer">
      <div>Culinaire Specialist : culinairespecialist.com</div>
      <div style="text-align:right;">Generated <span class="value">${escapeHtml(dateStr)}</span></div>
    </div>
  </div>

  <script>
    const summaryWith = ${JSON.stringify(summaryTextWithCost)};
    const summaryNo = ${JSON.stringify(summaryTextNoCost)};
    const csvWith = ${JSON.stringify(csvWithCost)};
    const csvNo = ${JSON.stringify(csvNoCost)};
    const csvName = ${JSON.stringify(csvName)};
    const restaurant = ${JSON.stringify(restaurant || "Kitchen")};
    const period = ${JSON.stringify(layout.subtitle)};
    const okEl = document.getElementById("ok");
    const toggle = document.getElementById("cost-toggle");
    function flash(msg) {
      okEl.textContent = msg;
      okEl.classList.add("show");
      setTimeout(() => okEl.classList.remove("show"), 1800);
    }
    function includeCost() { return toggle.checked; }
    toggle.addEventListener("change", () => {
      document.body.classList.toggle("show-cost", toggle.checked);
    });
    document.getElementById("csv-btn").addEventListener("click", () => {
      try {
        const text = includeCost() ? csvWith : csvNo;
        const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = csvName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 4000);
        flash(includeCost() ? "CSV downloaded (with labor cost)" : "CSV downloaded (no labor cost)");
      } catch (e) {
        flash("CSV download failed");
      }
    });
    document.getElementById("copy-btn").addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(includeCost() ? summaryWith : summaryNo);
        flash("Summary copied to clipboard");
      } catch (_) {
        flash("Copy failed. Select and copy manually.");
      }
    });
    if (navigator.share) {
      const btn = document.getElementById("share-btn");
      btn.style.display = "inline-block";
      btn.addEventListener("click", async () => {
        try {
          await navigator.share({
            title: restaurant + " : Roster : " + period,
            text: includeCost() ? summaryWith : summaryNo,
          });
        } catch (_) { /* user cancelled */ }
      });
    }
  </script>
</body></html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}
